import {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { TextField } from '@mui/material';
import { usePickerContext } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import FormLabel from './FormLabel';
import ClearInputAdornment from './ClearInputAdornment';
import { CustomDateRangePickerAdornment } from './DateRangePicker';
import DisabledTooltip from '@components/DisabledTooltip';
import classNames from 'classnames/bind';

import styles from './Form.module.css';
const styleContext = classNames.bind(styles);

// ─── Segment Definitions ────────────────────────────────────────────

interface SegmentDef {
	/** Start index (inclusive) in the display string */
	start: number;
	/** End index (exclusive) in the display string */
	end: number;
	/** Placeholder text when segment has no value */
	placeholder: string;
	/** Minimum valid value */
	min: number;
	/** Maximum valid value (may be dynamic for Day) */
	max: number;
	/** Dayjs unit this segment maps to */
	unit: 'month' | 'date' | 'year' | 'hour' | 'minute';
	/** Which side of the range this segment belongs to */
	rangePosition: 'from' | 'to';
}

/**
 * 7 editable segments in the format: MM/DD/YY HH:mm - HH:mm
 *
 * Character positions:
 *   M M / D D / Y Y   H H : m m   -   H H : m m
 *   0 1 2 3 4 5 6 7 8 9 0 1 2 3 4 5 6 7 8 9 0 1
 */
const SEGMENTS: SegmentDef[] = [
	{
		start: 0,
		end: 2,
		placeholder: 'MM',
		min: 1,
		max: 12,
		unit: 'month',
		rangePosition: 'from'
	},
	{
		start: 3,
		end: 5,
		placeholder: 'DD',
		min: 1,
		max: 31,
		unit: 'date',
		rangePosition: 'from'
	},
	{
		start: 6,
		end: 8,
		placeholder: 'YY',
		min: 0,
		max: 99,
		unit: 'year',
		rangePosition: 'from'
	},
	{
		start: 9,
		end: 11,
		placeholder: 'HH',
		min: 0,
		max: 23,
		unit: 'hour',
		rangePosition: 'from'
	},
	{
		start: 12,
		end: 14,
		placeholder: 'mm',
		min: 0,
		max: 59,
		unit: 'minute',
		rangePosition: 'from'
	},
	{
		start: 17,
		end: 19,
		placeholder: 'HH',
		min: 0,
		max: 23,
		unit: 'hour',
		rangePosition: 'to'
	},
	{
		start: 20,
		end: 22,
		placeholder: 'mm',
		min: 0,
		max: 59,
		unit: 'minute',
		rangePosition: 'to'
	}
];

// ─── Helpers ────────────────────────────────────────────────────────

function getSegmentValue(d: Dayjs, seg: SegmentDef): number {
	switch (seg.unit) {
		case 'month':
			return d.month() + 1;
		case 'date':
			return d.date();
		case 'year':
			return d.year() % 100;
		case 'hour':
			return d.hour();
		case 'minute':
			return d.minute();
	}
}

function getSegmentText(d: Dayjs | null, seg: SegmentDef): string {
	if (!d?.isValid()) return seg.placeholder;
	return String(getSegmentValue(d, seg)).padStart(2, '0');
}

function applySegmentValue(d: Dayjs, seg: SegmentDef, value: number): Dayjs {
	switch (seg.unit) {
		case 'month':
			return d.month(value - 1);
		case 'date':
			return d.date(value);
		case 'year':
			return d.year(2000 + value);
		case 'hour':
			return d.hour(value);
		case 'minute':
			return d.minute(value);
	}
}

function getSegmentMax(seg: SegmentDef, from: Dayjs | null): number {
	if (seg.unit === 'date' && from?.isValid()) {
		return from.daysInMonth();
	}
	return seg.max;
}

function buildDisplayText(
	from: Dayjs | null,
	to: Dayjs | null,
	activeIdx: number | null,
	pendingDigit: number | null
): string {
	if (!from && !to && activeIdx === null) return '';

	const parts = SEGMENTS.map((seg, i) => {
		if (i === activeIdx && pendingDigit !== null) {
			return `${String(pendingDigit)}\u2007`; // figure space for the pending second digit
		}
		const target = seg.rangePosition === 'from' ? from : to;
		return getSegmentText(target, seg);
	});

	return `${parts[0]}/${parts[1]}/${parts[2]} ${parts[3]}:${parts[4]} - ${parts[5]}:${parts[6]}`;
}

function findSegmentAtPosition(cursorPos: number): number {
	for (let i = 0; i < SEGMENTS.length; i++) {
		if (cursorPos >= SEGMENTS[i].start && cursorPos < SEGMENTS[i].end) {
			return i;
		}
	}
	let closest = 0;
	let minDist = Infinity;
	for (let i = 0; i < SEGMENTS.length; i++) {
		const mid = (SEGMENTS[i].start + SEGMENTS[i].end) / 2;
		const dist = Math.abs(cursorPos - mid);
		if (dist < minDist) {
			minDist = dist;
			closest = i;
		}
	}
	return closest;
}

// ─── Props ──────────────────────────────────────────────────────────

export interface SegmentedFieldProps {
	isDirty?: boolean;
	isError?: boolean;
	errorText?: string;
	labelText?: string;
	variant?: 'outlined' | 'standard' | 'filled';
	onClear?: () => void;
	required?: boolean;
	disabledReason?: string;
	disabledClass?: string;
	onBlurField?: React.FocusEventHandler;
}

// ─── Component ──────────────────────────────────────────────────────

const SegmentedDateTimeRangeField = forwardRef<
	HTMLDivElement,
	SegmentedFieldProps
>(function SegmentedDateTimeRangeField(
	{
		isDirty,
		isError,
		errorText,
		labelText,
		variant = 'outlined',
		onClear,
		required,
		disabledReason,
		disabledClass,
		onBlurField
	},
	ref
) {
	const pickerCtx = usePickerContext();
	const rangeValue = pickerCtx.value as unknown as
		| [Dayjs | null, Dayjs | null]
		| undefined;
	const from = rangeValue?.[0] ?? null;
	const to = rangeValue?.[1] ?? null;

	const inputRef = useRef<HTMLInputElement>(null);
	const [activeSegment, setActiveSegment] = useState<number | null>(null);
	const [pendingDigit, setPendingDigit] = useState<number | null>(null);

	const displayText = useMemo(
		() => buildDisplayText(from, to, activeSegment, pendingDigit),
		[from, to, activeSegment, pendingDigit]
	);

	// Re-apply text selection after every render that changes the active segment or display
	useEffect(() => {
		if (
			activeSegment !== null &&
			inputRef.current === document.activeElement
		) {
			const seg = SEGMENTS[activeSegment];
			requestAnimationFrame(() => {
				inputRef.current?.setSelectionRange(seg.start, seg.end);
			});
		}
	}, [activeSegment, displayText]);

	const ensureValues = useCallback((): [Dayjs, Dayjs] => {
		const skeleton = dayjs().startOf('day');
		return [
			from?.isValid() ? from : skeleton,
			to?.isValid() ? to : (from?.isValid() ? from : skeleton)
		];
	}, [from, to]);

	const updateValue = useCallback(
		(newFrom: Dayjs, newTo: Dayjs) => {
			pickerCtx.setValue(
				[newFrom, newTo] as unknown as Dayjs,
				{ changeImportance: 'set' }
			);
		},
		[pickerCtx]
	);

	const advanceSegment = useCallback(() => {
		setActiveSegment((prev) =>
			prev !== null && prev < 6 ? prev + 1 : prev
		);
	}, []);

	const applyPendingAndUpdate = useCallback(
		(
			seg: SegmentDef,
			value: number,
			f: Dayjs,
			t: Dayjs,
			advance: boolean
		) => {
			const max = getSegmentMax(seg, f);
			const clamped = Math.max(seg.min, Math.min(value, max));
			const target = seg.rangePosition === 'from' ? f : t;
			const updated = applySegmentValue(target, seg, clamped);
			if (seg.rangePosition === 'from') {
				updateValue(updated, t);
			} else {
				updateValue(f, updated);
			}
			setPendingDigit(null);
			if (advance) advanceSegment();
		},
		[updateValue, advanceSegment]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (pickerCtx.disabled || pickerCtx.readOnly) return;
			if (activeSegment === null) return;

			const seg = SEGMENTS[activeSegment];

			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault();
					setPendingDigit(null);
					setActiveSegment(Math.max(0, activeSegment - 1));
					break;

				case 'ArrowRight':
					e.preventDefault();
					setPendingDigit(null);
					setActiveSegment(Math.min(6, activeSegment + 1));
					break;

				case 'Tab':
					if (e.shiftKey && activeSegment > 0) {
						e.preventDefault();
						setPendingDigit(null);
						setActiveSegment(activeSegment - 1);
					} else if (!e.shiftKey && activeSegment < 6) {
						e.preventDefault();
						setPendingDigit(null);
						setActiveSegment(activeSegment + 1);
					}
					// else: allow default Tab to leave the input
					break;

				case 'ArrowUp':
				case 'ArrowDown': {
					e.preventDefault();
					const delta = e.key === 'ArrowUp' ? 1 : -1;
					const [f, t] = ensureValues();
					const target =
						seg.rangePosition === 'from' ? f : t;
					const current = getSegmentValue(target, seg);
					const max = getSegmentMax(seg, f);
					let next = current + delta;
					if (next > max) next = seg.min;
					if (next < seg.min) next = max;
					const updated = applySegmentValue(
						target,
						seg,
						next
					);
					if (seg.rangePosition === 'from') {
						updateValue(updated, t);
					} else {
						updateValue(f, updated);
					}
					setPendingDigit(null);
					break;
				}

				case 'Backspace':
				case 'Delete':
					e.preventDefault();
					pickerCtx.clearValue();
					setPendingDigit(null);
					break;

				default:
					if (/^[0-9]$/.test(e.key)) {
						e.preventDefault();
						const digit = parseInt(e.key, 10);
						const [f, t] = ensureValues();
						const max = getSegmentMax(seg, f);

						if (pendingDigit === null) {
							if (digit * 10 > max) {
								// Single digit completes the segment
								applyPendingAndUpdate(
									seg,
									digit,
									f,
									t,
									true
								);
							} else {
								setPendingDigit(digit);
							}
						} else {
							const fullValue =
								pendingDigit * 10 + digit;
							applyPendingAndUpdate(
								seg,
								fullValue,
								f,
								t,
								true
							);
						}
					}
			}
		},
		[
			activeSegment,
			pendingDigit,
			pickerCtx,
			ensureValues,
			updateValue,
			applyPendingAndUpdate
		]
	);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLInputElement>) => {
			const pos = e.currentTarget.selectionStart ?? 0;
			setPendingDigit(null);
			setActiveSegment(findSegmentAtPosition(pos));
		},
		[]
	);

	const handleFocus = useCallback(() => {
		if (activeSegment === null) {
			setActiveSegment(0);
		}
	}, [activeSegment]);

	const handleBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			// Don't deactivate if focus moved to the popup
			if (pickerCtx.open) return;

			// Flush pending digit on blur
			if (pendingDigit !== null && activeSegment !== null) {
				const seg = SEGMENTS[activeSegment];
				const [f, t] = ensureValues();
				applyPendingAndUpdate(seg, pendingDigit, f, t, false);
			}

			setPendingDigit(null);
			setActiveSegment(null);
			onBlurField?.(e);
		},
		[
			pickerCtx.open,
			pendingDigit,
			activeSegment,
			ensureValues,
			applyPendingAndUpdate,
			onBlurField
		]
	);

	const classes = styleContext({
		input: true,
		dirty: isDirty,
		error: isError,
		disabled: pickerCtx.disabled,
		empty: !from && !to
	});

	return (
		<DisabledTooltip
			message={disabledReason}
			className={disabledClass}
		>
			<TextField
				ref={ref}
				inputRef={inputRef}
				value={displayText}
				placeholder='MM/DD/YY HH:mm - HH:mm'
				onKeyDown={handleKeyDown}
				onClick={handleClick}
				onFocus={handleFocus}
				onBlur={handleBlur}
				// eslint-disable-next-line @typescript-eslint/no-empty-function -- controlled input; keyboard handler manages all changes
				onChange={() => {}}
				disabled={pickerCtx.disabled}
				name={pickerCtx.name ?? undefined}
				error={isError}
				className={classes}
				variant={variant}
				label={
					<FormLabel
						text={labelText ?? ''}
						error={isError}
						errorText={errorText}
						required={required}
					/>
				}
				slotProps={{
					input: {
						ref: pickerCtx.triggerRef as React.Ref<HTMLDivElement>,
						className: styleContext({
							root: true,
							input: true,
							'date-time-range-picker': true
						}),
						startAdornment: (
							<CustomDateRangePickerAdornment />
						),
						endAdornment: (
							<ClearInputAdornment
								onClear={
									onClear ??
									(() => {
										pickerCtx.clearValue();
									})
								}
								inputValue={from ?? to}
								inputIsDisabled={pickerCtx.disabled}
							/>
						)
					}
				}}
			/>
		</DisabledTooltip>
	);
});

export default SegmentedDateTimeRangeField;
